/**
 * Backoffice pilotage PWA 2i Recouvrement (agents + audit trail + code d'accès).
 */
import React, { useEffect, useState } from 'react';
import {
  Users, ClipboardList, KeyRound, RefreshCw, ShieldOff, ShieldCheck,
  Trash2, AlertCircle, CheckCircle2, Copy, Save, ScanLine,
  Search, Calendar,
} from 'lucide-react';
import {
  listAgents, toggleAgent, deleteAgent, fusionnerDoublons,
  listVerifications, getCodeAcces, setCodeAcces, getStatsJournalier,
  AgentRecouvrement, VerifRecord, StatsJournalier,
} from './api';
import { GitMerge } from 'lucide-react';

type Tab = 'agents' | 'verifications' | 'code';

export default function Recouvrement() {
  const [tab, setTab] = useState<Tab>('agents');

  return (
    <div className="p-4 max-w-6xl mx-auto">
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <ScanLine size={22} className="text-blue-700" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Recouvrement — Vérification étudiants
            </h1>
          </div>
          <p className="text-sm text-gray-500">
            Pilotage de la PWA 2i Recouvrement utilisée par les agents de recouvrement sur le terrain.
          </p>
        </div>

        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-4">
          <TabBtn active={tab === 'agents'} onClick={() => setTab('agents')} icon={<Users size={14} />}>
            Agents
          </TabBtn>
          <TabBtn active={tab === 'verifications'} onClick={() => setTab('verifications')} icon={<ClipboardList size={14} />}>
            Historique vérifications
          </TabBtn>
          <TabBtn active={tab === 'code'} onClick={() => setTab('code')} icon={<KeyRound size={14} />}>
            Code d'accès
          </TabBtn>
        </div>

      {tab === 'agents' && <AgentsPanel />}
      {tab === 'verifications' && <VerificationsPanel />}
      {tab === 'code' && <CodePanel />}
    </div>
  );
}

const TabBtn: React.FC<{
  active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode;
}> = ({ active, onClick, icon, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center gap-1.5 transition ${
      active
        ? 'border-blue-600 text-blue-700 dark:text-blue-400'
        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
    }`}
  >
    {icon}{children}
  </button>
);

// ---------------------------------------------------------------------------
// AGENTS
// ---------------------------------------------------------------------------

const AgentsPanel: React.FC = () => {
  const [agents, setAgents] = useState<AgentRecouvrement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await listAgents();
      setAgents(r.results);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Erreur chargement');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onToggle = async (a: AgentRecouvrement) => {
    const nextActif = !a.actif;
    let motif = '';
    if (!nextActif) {
      motif = window.prompt(`Motif de désactivation de ${a.nom_complet} ?`, '') || '';
    }
    try {
      await toggleAgent(a.id, nextActif, motif);
      load();
    } catch (e: any) {
      alert('Erreur : ' + (e?.message || 'inconnue'));
    }
  };

  const onDelete = async (a: AgentRecouvrement) => {
    if (!window.confirm(`Supprimer définitivement ${a.nom_complet} ?\n\nL'agent doit n'avoir aucune vérification enregistrée.`)) return;
    try {
      await deleteAgent(a.id);
      load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || e?.message || 'Suppression refusée');
    }
  };

  // Détection doublons (même nom + prénoms, insensible casse)
  const doublons = React.useMemo(() => {
    const map = new Map<string, number>();
    agents.forEach(a => {
      const k = `${a.nom.trim().toUpperCase()}|${a.prenoms.trim().toLowerCase()}`;
      map.set(k, (map.get(k) || 0) + 1);
    });
    return Array.from(map.values()).filter(n => n > 1).reduce((s, n) => s + (n - 1), 0);
  }, [agents]);

  const onFusionner = async () => {
    if (!window.confirm(`Fusionner ${doublons} doublon${doublons > 1 ? 's' : ''} ?\n\nPour chaque groupe (même nom + prénoms), l'agent le plus ancien est conservé et l'historique de vérifications des doublons y est transféré. Les doublons sont ensuite supprimés.`)) return;
    try {
      const r = await fusionnerDoublons();
      alert(`Fusion terminée : ${r.total_supprimes} doublon${r.total_supprimes > 1 ? 's' : ''} supprimé${r.total_supprimes > 1 ? 's' : ''}.`);
      load();
    } catch (e: any) {
      alert('Erreur : ' + (e?.response?.data?.detail || e?.message || 'inconnue'));
    }
  };

  return (
    <div>
      <div className="flex justify-between mb-3">
        <div className="text-sm text-gray-500">
          {agents.length} agent{agents.length > 1 ? 's' : ''} enregistré{agents.length > 1 ? 's' : ''}
          {doublons > 0 && (
            <span className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold">
              <AlertCircle size={11} /> {doublons} doublon{doublons > 1 ? 's' : ''} détecté{doublons > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex gap-3">
          {doublons > 0 && (
            <button
              onClick={onFusionner}
              className="text-sm text-amber-700 hover:text-amber-800 flex items-center gap-1 font-semibold"
            >
              <GitMerge size={13} /> Fusionner les doublons
            </button>
          )}
          <button onClick={load} className="text-sm text-blue-700 flex items-center gap-1 hover:text-blue-800">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Rafraîchir
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm mb-3">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Agent</th>
              <th className="px-3 py-2 text-left">Téléphone</th>
              <th className="px-3 py-2 text-left">Vérifs</th>
              <th className="px-3 py-2 text-left">Sessions actives</th>
              <th className="px-3 py-2 text-left">Dernier ping</th>
              <th className="px-3 py-2 text-left">Appareil</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {agents.map(a => (
              <tr key={a.id} className={a.actif ? '' : 'opacity-60 bg-red-50 dark:bg-red-900/10'}>
                <td className="px-3 py-2">
                  <div className="font-semibold text-gray-900 dark:text-white">{a.nom_complet}</div>
                  <div className="text-[11px] text-gray-500">
                    Inscrit le {new Date(a.date_enregistrement).toLocaleDateString('fr-FR')}
                    {!a.actif && a.motif_desactivation && (
                      <span className="text-red-600 ml-2">· Désactivé : {a.motif_desactivation}</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">{a.telephone || '—'}</td>
                <td className="px-3 py-2 font-mono">{a.nb_verifications.toLocaleString('fr-FR')}</td>
                <td className="px-3 py-2">
                  {a.nb_sessions_actives > 0
                    ? <span className="text-emerald-700 font-semibold">{a.nb_sessions_actives}</span>
                    : <span className="text-gray-400">0</span>}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                  {a.dernier_ping
                    ? new Date(a.dernier_ping).toLocaleString('fr-FR')
                    : <span className="text-gray-400">jamais</span>}
                </td>
                <td className="px-3 py-2 text-[10px] font-mono text-gray-500">
                  {a.device_id_short}
                  <div className="text-[10px] text-gray-400">{a.ip_derniere}</div>
                </td>
                <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                  <button
                    onClick={() => onToggle(a)}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                      a.actif
                        ? 'bg-orange-100 hover:bg-orange-200 text-orange-800'
                        : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800'
                    }`}
                    title={a.actif ? 'Révoquer' : 'Réactiver'}
                  >
                    {a.actif ? <><ShieldOff size={11} /> Révoquer</> : <><ShieldCheck size={11} /> Réactiver</>}
                  </button>
                  <button
                    onClick={() => onDelete(a)}
                    disabled={a.nb_verifications > 0}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-100 hover:bg-red-200 text-red-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    title={a.nb_verifications > 0 ? 'A des vérifications — désactiver plutôt' : 'Supprimer'}
                  >
                    <Trash2 size={11} />
                  </button>
                </td>
              </tr>
            ))}
            {agents.length === 0 && !loading && (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">Aucun agent enregistré.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// VÉRIFICATIONS
// ---------------------------------------------------------------------------

const VerificationsPanel: React.FC = () => {
  const [data, setData] = useState<{ results: VerifRecord[]; total: number; stats: any } | null>(null);
  const [stats, setStats] = useState<StatsJournalier | null>(null);
  const [agents, setAgents] = useState<AgentRecouvrement[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    agent: '' as string | number, matricule: '', depuis: '', jusqu: '', limit: 200,
  });

  // Charger la liste des agents pour le select
  useEffect(() => {
    listAgents().then(r => setAgents(r.results)).catch(() => {});
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { ...filters };
      if (!params.agent) delete params.agent;
      const r = await listVerifications(params);
      setData(r);
      // Stats journalier (30 derniers jours), filtrées par agent si sélectionné
      const s = await getStatsJournalier(30, filters.agent ? Number(filters.agent) : undefined);
      setStats(s);
    } catch (e) { /* silence */ }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Grille : max des totaux journaliers → hauteur des barres
  const maxJour = stats?.par_jour?.reduce((m, j) => Math.max(m, j.total), 0) || 1;

  return (
    <div>
      {/* Stats globales */}
      {data?.stats && (
        <div className="grid grid-cols-4 gap-2 mb-4">
          <StatCard label="Total (période)" value={stats?.total_periode.toLocaleString('fr-FR') ?? '—'} />
          <StatCard label="Total (toutes)" value={data.total.toLocaleString('fr-FR')} />
          <StatCard label="Agents actifs 24h" value={data.stats.nb_agents_actifs_24h ?? 0} />
          <StatCard label="Méthodes distinctes" value={Object.keys(data.stats.par_methode || {}).length} />
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3 grid grid-cols-5 gap-2 text-sm">
        <div>
          <label className="text-[11px] text-gray-500 uppercase font-semibold flex items-center gap-1">
            <Users size={11} /> Agent
          </label>
          <select
            value={filters.agent}
            onChange={(e) => setFilters(f => ({ ...f, agent: e.target.value }))}
            className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
          >
            <option value="">Tous les agents</option>
            {agents.map(a => (
              <option key={a.id} value={a.id}>
                {a.nom_complet}{!a.actif ? ' (révoqué)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] text-gray-500 uppercase font-semibold flex items-center gap-1">
            <Search size={11} /> Matricule
          </label>
          <input
            type="text"
            value={filters.matricule}
            onChange={(e) => setFilters(f => ({ ...f, matricule: e.target.value }))}
            className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
          />
        </div>
        <div>
          <label className="text-[11px] text-gray-500 uppercase font-semibold flex items-center gap-1">
            <Calendar size={11} /> Depuis
          </label>
          <input
            type="date"
            value={filters.depuis}
            onChange={(e) => setFilters(f => ({ ...f, depuis: e.target.value }))}
            className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
          />
        </div>
        <div>
          <label className="text-[11px] text-gray-500 uppercase font-semibold flex items-center gap-1">
            <Calendar size={11} /> Jusqu'au
          </label>
          <input
            type="date"
            value={filters.jusqu}
            onChange={(e) => setFilters(f => ({ ...f, jusqu: e.target.value }))}
            className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
          />
        </div>
        <div>
          <label className="text-[11px] text-gray-500 uppercase font-semibold">Limite</label>
          <select
            value={filters.limit}
            onChange={(e) => setFilters(f => ({ ...f, limit: Number(e.target.value) }))}
            className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
          >
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
        </div>
      </div>

      {/* Graphe stats journalier + top agents */}
      {stats && stats.par_jour.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1">
              <ClipboardList size={14} /> Activité — {stats.periode_jours} derniers jours
              {filters.agent && (() => {
                const a = agents.find(x => x.id === Number(filters.agent));
                return a ? <span className="text-xs text-blue-700 ml-1">· {a.nom_complet}</span> : null;
              })()}
            </div>
            <div className="text-xs text-gray-500">
              Total : <b>{stats.total_periode.toLocaleString('fr-FR')}</b>
            </div>
          </div>
          <div
            className="flex items-end gap-[2px] mt-2 bg-gray-50 dark:bg-gray-900/40 rounded p-2"
            style={{ height: '140px' }}
          >
            {stats.par_jour.map(j => {
              const h = j.total === 0 ? 3 : Math.max(6, Math.round((j.total / maxJour) * 100));
              const todayIso = new Date().toISOString().slice(0, 10);
              const isToday = j.date === todayIso;
              const isEmpty = j.total === 0;
              return (
                <div
                  key={j.date}
                  className="flex-1 flex flex-col items-center justify-end group relative min-w-0"
                  title={`${new Date(j.date).toLocaleDateString('fr-FR', {weekday:'short', day:'numeric', month:'short'})} — ${j.total} vérif${j.total > 1 ? 's' : ''}`}
                >
                  {/* Chiffre au-dessus de la barre (visible sur les barres non vides) */}
                  {!isEmpty && (
                    <div className="text-[9px] font-mono font-semibold text-gray-600 dark:text-gray-300 mb-0.5 leading-none">
                      {j.total}
                    </div>
                  )}
                  <div
                    className={`w-full rounded-t transition ${
                      isEmpty
                        ? 'bg-gray-200 dark:bg-gray-700'
                        : isToday
                          ? 'bg-emerald-500 hover:bg-emerald-600'
                          : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                    style={{ height: `${h}%`, minHeight: isEmpty ? '3px' : '6px' }}
                  />
                </div>
              );
            })}
          </div>
          {/* Labels dates : début, milieu, fin */}
          <div className="text-[10px] text-gray-400 flex justify-between mt-1 px-2">
            {(() => {
              const items = stats.par_jour;
              if (items.length === 0) return null;
              const first = items[0];
              const mid = items[Math.floor(items.length / 2)];
              const last = items[items.length - 1];
              const fmt = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', {day:'numeric', month:'short'});
              return (
                <>
                  <span>{fmt(first.date)}</span>
                  <span>{fmt(mid.date)}</span>
                  <span className="text-emerald-600 font-semibold">{fmt(last.date)} (auj.)</span>
                </>
              );
            })()}
          </div>

          {/* Top agents (seulement si pas déjà filtré par agent) */}
          {!filters.agent && stats.top_agents.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Top agents (période)</div>
              <div className="space-y-1">
                {stats.top_agents.slice(0, 5).map(a => (
                  <div key={a.agent_id} className="flex items-center gap-2 text-sm">
                    <div className="flex-1 truncate">{a.nom_complet}</div>
                    <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                      <div
                        className="h-full bg-blue-500"
                        style={{ width: `${Math.round((a.total / stats.top_agents[0].total) * 100)}%` }}
                      />
                    </div>
                    <div className="text-xs font-mono font-semibold w-12 text-right">{a.total}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Liste */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Date / heure</th>
              <th className="px-3 py-2 text-left">Agent</th>
              <th className="px-3 py-2 text-left">Étudiant</th>
              <th className="px-3 py-2 text-left">Matricule</th>
              <th className="px-3 py-2 text-left">Classe</th>
              <th className="px-3 py-2 text-left">Méthode</th>
              <th className="px-3 py-2 text-left">Statut</th>
              <th className="px-3 py-2 text-left">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {data?.results.map(v => (
              <tr key={v.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-3 py-2 text-xs">
                  {new Date(v.date).toLocaleString('fr-FR')}
                </td>
                <td className="px-3 py-2 text-sm font-semibold">{v.agent_nom}</td>
                <td className="px-3 py-2 text-sm">{v.etudiant_nom || '—'}</td>
                <td className="px-3 py-2 text-xs font-mono">{v.matricule}</td>
                <td className="px-3 py-2 text-xs">{v.etudiant_classe || '—'}</td>
                <td className="px-3 py-2 text-[11px]">
                  <span className="inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                    {v.methode_libelle}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {v.etait_a_jour === true && <span className="text-emerald-700 font-semibold">À jour</span>}
                  {v.etait_a_jour === false && <span className="text-red-700 font-semibold">Non à jour</span>}
                  {v.etait_a_jour == null && <span className="text-gray-400">—</span>}
                  {v.solde && Number(v.solde) > 0 && (
                    <div className="text-[10px] text-gray-500">
                      Solde : {Number(v.solde).toLocaleString('fr-FR')} F
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-[10px] font-mono text-gray-500">{v.ip}</td>
              </tr>
            ))}
            {data && data.results.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-gray-500">Aucune vérification.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
    <div className="text-[11px] uppercase text-gray-500 font-semibold">{label}</div>
    <div className="text-2xl font-bold text-blue-700 dark:text-blue-400 mt-1">{value}</div>
  </div>
);

// ---------------------------------------------------------------------------
// CODE D'ACCÈS
// ---------------------------------------------------------------------------

const CodePanel: React.FC = () => {
  const [current, setCurrent] = useState<string>('');
  const [newCode, setNewCode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    getCodeAcces().then(r => setCurrent(r.code)).finally(() => setLoading(false));
  }, []);

  const flashMsg = (msg: string) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), 3000);
  };

  const onCopy = () => {
    navigator.clipboard?.writeText(current);
    flashMsg('Code copié dans le presse-papier');
  };

  const onSave = async () => {
    const code = newCode.trim();
    if (code.length < 3 || code.length > 20) {
      alert('Le code doit faire entre 3 et 20 caractères.');
      return;
    }
    if (!window.confirm(`Confirmer le changement de code d'accès ?\n\nToutes les sessions agents actuellement ouvertes seront invalidées et les agents devront ressaisir le nouveau code.`)) return;
    setSaving(true);
    try {
      const r = await setCodeAcces(code);
      setCurrent(r.code);
      setNewCode('');
      flashMsg('Code mis à jour. Sessions révoquées.');
    } catch (e: any) {
      alert('Erreur : ' + (e?.response?.data?.detail || e?.message || 'inconnue'));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-gray-700">
          <KeyRound size={18} className="text-blue-700" />
          <div className="font-semibold text-lg">Code d'accès agents</div>
        </div>

        {flash && (
          <div className="bg-emerald-50 border border-emerald-300 text-emerald-800 rounded p-2 text-sm flex items-center gap-2">
            <CheckCircle2 size={14} /> {flash}
          </div>
        )}

        <div>
          <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Code actuel</div>
          {loading ? (
            <div className="h-10 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-lg py-3 px-4 font-mono text-xl tracking-widest text-center text-blue-900 dark:text-blue-200">
                {current || '—'}
              </div>
              <button
                onClick={onCopy}
                className="p-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg"
                title="Copier"
              >
                <Copy size={16} />
              </button>
            </div>
          )}
          <div className="text-[11px] text-gray-500 mt-2 flex items-start gap-1">
            <AlertCircle size={11} className="flex-shrink-0 mt-0.5" />
            Communiquez ce code aux agents autorisés uniquement. En cas de fuite, changez-le
            ci-dessous — toutes les sessions actives seront invalidées immédiatement.
          </div>
        </div>

        <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Nouveau code</div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              className="flex-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-lg font-mono tracking-widest text-center bg-white dark:bg-gray-700"
              placeholder="Nouveau code (3-20 caractères)"
              maxLength={20}
            />
            <button
              onClick={onSave}
              disabled={saving || newCode.trim().length < 3}
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg font-semibold flex items-center gap-1"
            >
              <Save size={14} /> Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
